/**
 * Buyer Selection Card Component
 * Handles buyer search, selection, and user management for chat creation
 */

import type {
  BuyerSelectOption,
  CreateChatFormData,
  BuyerUserSelectOption,
} from '@choco-admin/types';
import {
  Button,
  Card,
  CardBody,
  Autocomplete,
  AutocompleteItem,
} from '@heroui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FiCheck, FiUser, FiUsers, FiX } from 'react-icons/fi';

import { Pill } from '../../../../../../components/Pill';
import { useGetBuyerUsers } from '../../../../hooks/useGetBuyerUsers';
import { useSearchBuyersForChat } from '../../../../hooks/useSearchBuyersForChat';
import {
  ENTITY_SEARCH_MIN_CHARS,
  ENTITY_SEARCH_MIN_CHARS_HINT,
} from '../../../shared/entitySearch';

interface BuyerSelectionCardProps {
  onBuyerSelect: (buyer: BuyerSelectOption) => void;
  onBuyerUserToggle: (user: BuyerUserSelectOption) => void;
  onRemoveBuyerUser: (userId: string) => void;
}

export const BuyerSelectionCard: React.FC<BuyerSelectionCardProps> = ({
  onBuyerSelect,
  onBuyerUserToggle,
  onRemoveBuyerUser,
}) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CreateChatFormData>();
  const formData = watch();

  // Entity search input value — used to hint the 3-character minimum
  const [buyerSearchInput, setBuyerSearchInput] = useState('');
  // Buyer-user filter input — users are pre-loaded, filtering is local and
  // starts from the first character (no minimum)
  const [buyerUserFilter, setBuyerUserFilter] = useState('');

  // Data hooks
  const {
    buyers,
    isLoading: buyersLoading,
    searchBuyers,
  } = useSearchBuyersForChat();

  const {
    buyerUsers,
    loading: buyerUsersLoading,
    getBuyerUsers,
  } = useGetBuyerUsers();

  const selectedBuyerUserIds = useMemo(
    () => new Set((formData.buyerUsers || []).map((user) => user.value)),
    [formData.buyerUsers]
  );

  const filteredBuyerUsers = useMemo(() => {
    const query = buyerUserFilter.trim().toLowerCase();
    if (!query) return buyerUsers;
    return buyerUsers.filter(
      (user) =>
        user.label.toLowerCase().includes(query) ||
        (user.metaData?.email || '').toLowerCase().includes(query)
    );
  }, [buyerUsers, buyerUserFilter]);

  // Fetch buyer users when a buyer is selected
  useEffect(() => {
    if (formData.buyer?.value) {
      getBuyerUsers(formData.buyer.value);
    }
  }, [formData.buyer?.value, getBuyerUsers]);

  return (
    <Card>
      <CardBody className="space-y-4">
        <div className="flex items-center gap-2">
          <FiUser className="text-primary" />
          <h4 className="font-medium">Buyer *</h4>
        </div>

        <Controller
          name="buyer"
          control={control}
          render={({ field, fieldState }) => (
            <Autocomplete
              placeholder="Type to search buyers..."
              aria-label="Search and select buyer"
              isLoading={buyersLoading}
              errorMessage={fieldState.error?.message}
              isInvalid={!!fieldState.error}
              description={
                buyerSearchInput.length > 0 &&
                buyerSearchInput.length < ENTITY_SEARCH_MIN_CHARS
                  ? ENTITY_SEARCH_MIN_CHARS_HINT
                  : undefined
              }
              selectedKey={field.value?.value || null}
              items={buyers}
              onInputChange={(value) => {
                setBuyerSearchInput(value);
                // Entity search only fires from the 3rd character
                if (value.length >= ENTITY_SEARCH_MIN_CHARS) {
                  searchBuyers(value);
                }
              }}
              onSelectionChange={(key) => {
                if (key) {
                  const selectedBuyer = buyers.find(
                    (buyer: BuyerSelectOption) => buyer.value === key
                  );
                  if (selectedBuyer) {
                    onBuyerSelect(selectedBuyer);
                  }
                } else {
                  setValue('buyer', null, { shouldValidate: true });
                  setValue('buyerUsers', [], { shouldValidate: true });
                }
              }}
              classNames={{
                base: 'max-w-full',
              }}
            >
              {(buyer: BuyerSelectOption) => (
                <AutocompleteItem
                  key={buyer.value}
                  textValue={buyer.label}
                  className="py-3 border-b border-divider last:border-b-0"
                >
                  <div className="flex flex-col py-1">
                    <span className="text-small font-medium text-foreground">
                      {buyer.label}
                    </span>
                    {buyer.metaData?.fullAddress && (
                      <span className="text-tiny text-default-600 mt-1">
                        {buyer.metaData.fullAddress}
                      </span>
                    )}
                  </div>
                </AutocompleteItem>
              )}
            </Autocomplete>
          )}
        />

        {/* Selected Buyer */}
        {formData.buyer && (
          <Card className="bg-primary-50 border-primary-200">
            <CardBody className="p-3">
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-primary-800 break-words">
                    {formData.buyer.label}
                  </div>
                  {formData.buyer.metaData?.fullAddress && (
                    <div className="text-xs text-primary-600 mt-1 break-words">
                      {formData.buyer.metaData.fullAddress}
                    </div>
                  )}
                </div>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  className="flex-shrink-0"
                  onPress={() => {
                    setValue('buyer', null, { shouldValidate: true });
                    setValue('buyerUsers', [], { shouldValidate: true });
                  }}
                >
                  <FiX />
                </Button>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Buyer Users */}
        {formData.buyer && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <FiUsers className="text-default-500" />
              <span className="text-sm font-medium">Buyer Users *</span>
              <span className="text-xs text-default-600">
                (at least one required)
              </span>
            </div>

            <Autocomplete
              placeholder="Search buyer users..."
              aria-label="Search and select buyer users"
              isLoading={buyerUsersLoading}
              selectedKey={null}
              items={filteredBuyerUsers}
              onInputChange={(value) => {
                // Buyer users are pre-loaded; filter locally from the first character
                setBuyerUserFilter(value);
              }}
              onSelectionChange={(key) => {
                if (key) {
                  const selectedUser = buyerUsers.find(
                    (user: BuyerUserSelectOption) => user.value === key
                  );
                  if (selectedUser) {
                    onBuyerUserToggle(selectedUser);
                  }
                }
              }}
              classNames={{
                base: 'max-w-full',
              }}
            >
              {(user: BuyerUserSelectOption) => {
                const isSelected = selectedBuyerUserIds.has(user.value);
                return (
                  <AutocompleteItem
                    key={user.value}
                    textValue={user.label}
                    className={`py-3 border-b border-divider last:border-b-0 ${
                      isSelected ? 'bg-primary-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 py-1">
                      <div className="flex flex-col min-w-0">
                        <span
                          className={`text-small font-medium truncate ${
                            isSelected ? 'text-primary' : 'text-foreground'
                          }`}
                        >
                          {user.label}
                        </span>
                        {user.metaData?.email && (
                          <span className="text-tiny text-default-600 mt-1 truncate">
                            {user.metaData.email}
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <FiCheck
                          className="text-primary flex-shrink-0"
                          aria-label="Selected"
                        />
                      )}
                    </div>
                  </AutocompleteItem>
                );
              }}
            </Autocomplete>

            {errors.buyerUsers?.message && (
              <p className="text-tiny text-danger">
                {errors.buyerUsers.message}
              </p>
            )}

            {/* Selected Buyer Users */}
            {formData.buyerUsers && formData.buyerUsers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.buyerUsers.map((user) => (
                  <Pill
                    key={user.value}
                    variant="primary"
                    size="sm"
                    className="max-w-full"
                    onClick={() => onRemoveBuyerUser(user.value)}
                  >
                    <div className="flex items-center gap-1">
                      <span className="break-words truncate">{user.label}</span>
                      <FiX className="text-xs cursor-pointer" />
                    </div>
                  </Pill>
                ))}
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
};
